import React, { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useState } from "react";

import { useTranslation } from "utils";

import { FieldListField } from "models";

import { InputSearch, Typography } from "components";

import { FieldListCard } from "pages/app/fields/components/FieldListCard";

type FieldSelectionListProps = {
  fields: FieldListField[];
  isFieldSelectable: (field: FieldListField) => boolean;
  selectedFields: string[];
  setSelectedFields: Dispatch<SetStateAction<string[]>>;
};

export const FieldSelectionList = ({ fields, isFieldSelectable, selectedFields, setSelectedFields }: FieldSelectionListProps) => {
  const { t } = useTranslation("farm");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleFieldSelect = (e: MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedFields.includes(id)) {
      const fieldsToSelect = selectedFields.filter((fieldId) => fieldId !== id);
      setSelectedFields(fieldsToSelect);
    } else {
      setSelectedFields([...selectedFields, id]);
    }
  };

  const fieldFilter = (field: FieldListField) => {
    if (!searchTerm) return true;
    const displayName = field.name || t("unknownFieldName");
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const filteredFields = fields.filter(fieldFilter);
  const selectableFields = filteredFields.filter(isFieldSelectable);

  const allSelected = selectableFields.every((field) => selectedFields.includes(field.id));

  const handleSelectAll = () => {
    setSelectedFields(selectableFields.map((field) => field.id));
  };

  const handleUnselectAll = () => {
    setSelectedFields([]);
  };

  const handleSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target?.value);
  };

  return (
    <>
      <div className="margin-bottom-200">
        <InputSearch value={searchTerm} label={t("fieldSelectionList.searchLabel")} onChange={handleSearchTermChange} />
      </div>
      {allSelected ? (
        <Typography variant="paragraph-m" bold onClick={handleUnselectAll} className=" margin-bottom-100 pointer">
          {t("fieldSelectionList.unselectAll")}
        </Typography>
      ) : (
        <Typography variant="paragraph-m" bold onClick={handleSelectAll} className="margin-bottom-100 pointer">
          {t("fieldSelectionList.selectAll")}
        </Typography>
      )}
      <div className="margin-bottom-100">
        {filteredFields.map((field, index) => (
          <div className="field-cardWithoutMethod margin-bottom-100" key={index}>
            <FieldListCard
              disabled={!isFieldSelectable(field)}
              field={field}
              isSelected={selectedFields.includes(field.id)}
              onSelectionChange={(e) => handleFieldSelect(e, field.id)}
            />
          </div>
        ))}
      </div>
    </>
  );
};
